import { useCallback, useEffect, type ReactNode } from 'react'
import { AUTH_FAILURE_EVENT, getAccessToken } from '@/lib/apiClient'
import { restoreSession, logout } from '@/lib/auth'
import { onNetworkChange } from '@/lib/platform/visibility'
import { connectSocket, disconnectSocket } from '@/lib/socketClient'
import { useAuthStore } from '@/lib/store'

/**
 * Порт mobile/providers/AuthProvider.tsx.
 *
 * Отличие: сессия не читается из хранилища, а поднимается запросом
 * /auth/refresh по httpOnly-куке — access-токен в браузере живёт только
 * в памяти и перезагрузку страницы не переживает.
 *
 * Плюс обработка провала обновления сессии: в мобильной версии при этом
 * вызывался только clearTokens(), а стор оставался с isAuthenticated: true,
 * и пользователь застревал в приложении, где каждый запрос отдавал 401.
 *
 * Провайдер только запускает восстановление сессии и НЕ держит экран
 * спиннером, пока оно идёт. Ждать результата обязаны только маршруты
 * кабинета — это делают ProtectedRoute и GuestRoute. Иначе лендинг
 * на «/» показывал бы гостю крутилку, пока по сети ходит запрос,
 * заведомо обречённый вернуть 401: куки у него нет.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const { setUser, setLoading, reset } = useAuthStore()

  const applySession = useCallback(async () => {
    const user = await restoreSession()
    if (user) {
      setUser(user)
      connectSocket()
    } else {
      reset()
    }
  }, [setUser, reset])

  useEffect(() => {
    let cancelled = false

    void (async () => {
      try {
        await applySession()
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [applySession, setLoading])

  /*
   * Запуск без сети поднимает сессию из кэша профиля: человек попадает
   * в кабинет и читает Библию, но access-токена у него нет. Когда сеть
   * возвращается, сессию надо проверить по-настоящему — и вот тут, если
   * она успела истечь, разлогинить уже честно.
   */
  useEffect(() => {
    return onNetworkChange((online) => {
      if (!online || getAccessToken()) return
      void applySession()
    })
  }, [applySession])

  useEffect(() => {
    const onAuthFailure = () => {
      disconnectSocket()
      reset()
    }

    window.addEventListener(AUTH_FAILURE_EVENT, onAuthFailure)
    return () => window.removeEventListener(AUTH_FAILURE_EVENT, onAuthFailure)
  }, [reset])

  // Выход в соседней вкладке должен разлогинивать и здесь.
  useEffect(() => {
    const channel = 'BroadcastChannel' in window ? new BroadcastChannel('lightchat-auth') : null

    if (channel) {
      channel.onmessage = (event) => {
        if (event.data === 'logout') {
          void logout().finally(() => reset())
        }
      }
    }

    return () => channel?.close()
  }, [reset])

  return <>{children}</>
}

/** Сообщает другим вкладкам, что пользователь вышел. */
export function broadcastLogout(): void {
  if ('BroadcastChannel' in window) {
    const channel = new BroadcastChannel('lightchat-auth')
    channel.postMessage('logout')
    channel.close()
  }
}
